import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Page,
    TopNav,
    NavBar,
    NavBrand,
    NavLogoSection,
    NavMenuSection,
    NavActionsSection,
    NavUser,
    NavNotification,
    NavUserProfile,
    NavUserName,
    ContentWrapper,
    Breadcrumb,
    HomeIconContainer,
    Children,
    HighLightButton
} from './PageContainer.styled';

import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import HomeIcon from '@mui/icons-material/Home';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps, AlertColor } from '@mui/material/Alert';
import { selectUserInfo, selectLogin } from '../SecureRoute/slice';
import { useAppSelector } from '../../redux/hooks';
import NavigationMenu from '../NavigationMenu/NavigationMenu';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export interface BreadCrumbLink {
    name: string,
    url: string
}

interface Props {
    title?: string
    breadcrumbs?: BreadCrumbLink[]
    message?: string
    children?: any
    open: boolean
    setOpen: (r: boolean) => void,
    severity?: AlertColor
}

const PageContainer: React.FC<Props> = ({
    title,
    breadcrumbs,
    children,
    message,
    open,
    setOpen,
    severity
}: Props): JSX.Element => {
    const navigate = useNavigate()
    const user = useAppSelector(selectUserInfo);
    const login = useAppSelector(selectLogin)

    const isSuperAdmin = login?.adminDTO?.type === 'SUPERADMIN';
    const isEaterAdmin = login?.adminDTO?.type === 'EATER' || login?.adminDTO?.type === 'SUPERADMIN';
    const isRestaurantAdmin = login?.adminDTO?.type === 'RESTAURANT' || login?.adminDTO?.type === 'EATER' || login?.adminDTO?.type === 'SUPERADMIN';

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string): void => {
        setOpen(false);
    };

  return (
    <Page>
        <TopNav>
            <NavBar>
                <NavLogoSection onClick={() => {
                    navigate('/')
                }}>
                    {isRestaurantAdmin && (
                        <>
                            <HighLightButton>
                                <span style={{ fontSize: '1.5rem' }}>🍴</span>
                            </HighLightButton>
                            <NavBrand>Resactly</NavBrand>
                        </>
                    )}
                </NavLogoSection>

                <NavMenuSection>
                    <NavigationMenu 
                        isSuperAdmin={isSuperAdmin}
                        isEaterAdmin={isEaterAdmin}
                        isRestaurantAdmin={isRestaurantAdmin}
                        currentTitle={title}
                    />
                </NavMenuSection>

                <NavActionsSection>
                    <NavUser>
                        <NavUserName>Hi {user?.nickname}!</NavUserName>
                        <NavNotification count={0} />
                        <NavUserProfile
                            onClick={() => {
                                navigate('/profile');
                            }}
                            profileImage={user?.picture}
                        />
                    </NavUser>
                </NavActionsSection>
            </NavBar>

            <ContentWrapper>
                <Breadcrumb>
                    <Breadcrumbs aria-label="breadcrumb">
                        <HomeIconContainer onClick={() => {navigate('/')}}>
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        </HomeIconContainer>
                        {(title !== 'Dashboard') && 
                            breadcrumbs?.map(b => {
                                return (<div
                                    key={b.name}
                                    onClick={() => {navigate(b.url)}}
                                >
                                {b.name}
                                </div>)
                        })}
                        {(title !== 'Dashboard') && 
                                <Typography
                                sx={{ display: 'flex', alignItems: 'center' }}
                                color="text.primary"
                                >
                                {title}
                                </Typography>
                        }
                    </Breadcrumbs>
                </Breadcrumb>
            </ContentWrapper>
        </TopNav>

        <Snackbar open={open} autoHideDuration={5000} onClose={handleClose}>
            <Alert onClose={handleClose} severity={severity??'success'} sx={{ width: '100%' }}>
                {message}
            </Alert>
        </Snackbar>

        <Children>
            {children}
        </Children>
    </Page>
  );
};

export default PageContainer;
